import { mergeClasses, Spinner } from "@fluentui/react-components";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { endOfDay } from "date-fns/endOfDay";
import { startOfDay } from "date-fns/startOfDay";
import { useContext, useEffect, useMemo, useState, type FC } from "react";
import { LayoutContext } from "../../../context/LayoutContext";
import { useNotificationWebSocket } from "../../../context/NotificationWebSocketContext";
import PageEnum from "../../../enum/PageEnum";
import { useApiResource } from "../../../hooks/useApiResource";
import { getAirportCode } from "../../../store/airport/AirportSelector";
import { screenNameActions } from "../../../store/screen/ScreenNameSlice";
import { useAppDispatch, useAppSelector } from "../../../store/StoreHooks";
import { CommonHelper } from "../../../utils/common/CommonHelper";
import { getAirportConfig } from "../../../utils/common/CommonHelperFunctions";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
} from "../../../utils/common/Constants";
import {
  getOldestNotificationDate,
  isNotificationInDateRange,
} from "../../../utils/notification/notificationHistory.utils";
import { formatFilteredNotifications } from "../../../utils/notification/NotificationPanelHelperFunctions";
import { NoDataCard } from "../../common/no-data-card/NoDataCard";
import { DEFAULT_FILTERS, DEFAULT_SORT } from "../constants";
import {
  NotificationFilterState,
  NotificationItem,
  NotificationSort,
} from "../types";
import NotificationHistoryFilters from "./NotificationHistoryFilters";
import NotificationHistoryHeader from "./NotificationHistoryHeader";
import NotificationHistoryList from "./NotificationHistoryList";
import { useNotificationHistoryStyles } from "./styles";

const notificationBaseUrl = `${process.env.REACT_APP_API_NOTIFICATION}`;

interface NotificationHistoryProps {
  allowMultipleExpanded?: boolean;
}

interface NotificationApiResponse {
  data: NotificationItem[];
}

const NotificationHistory: FC<NotificationHistoryProps> = ({
  allowMultipleExpanded = true,
}) => {
  const styles = useNotificationHistoryStyles();
  const isInTeams = CommonHelper.isInTeams();

  const airport = useAppSelector((state) => getAirportCode(state));
  const { timezone, currentTime } = useContext(LayoutContext);
  const { timeZoneId: airportTimeZone } = getAirportConfig(airport);
  const { notifications: socketNotifications } = useNotificationWebSocket();

  const currentYear = currentTime.getFullYear();
  const currentMonth = currentTime.getMonth();
  const currentDate = currentTime.getDate();
  const today = useMemo(
    () => startOfDay(new Date(currentYear, currentMonth, currentDate)),
    [currentDate, currentMonth, currentYear]
  );

  const [filters, setFilters] =
    useState<NotificationFilterState>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<NotificationSort>(DEFAULT_SORT);

  const [fromDate, setFromDate] = useState<Date>(today);
  const [toDate, setToDate] = useState<Date>(today);
  const [isDateRangeModified, setIsDateRangeModified] = useState(false);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const isLocalTime = timezone === "LT";

  const url =
    notificationBaseUrl?.replace(AIRPORT_CODE_PARAMETER, airport) || "";

  const { data, isFetching, isError } = useApiResource<NotificationApiResponse>(
    {
      url: url,
      queryKey: ["notifications-logs", airport],
      refetchOnWindowFocus: false,
    }
  );

  const notifications = useMemo(() => {
    const notificationsById = new Map<string, NotificationItem>();

    data?.data?.forEach((notification) => {
      notificationsById.set(notification.id, notification);
    });
    socketNotifications.forEach((notification) => {
      notificationsById.set(notification.id, notification);
    });

    return Array.from(notificationsById.values());
  }, [data, socketNotifications]);

  const initialFromDate = useMemo(() => {
    const oldestNotificationDate = getOldestNotificationDate(notifications);

    if (!oldestNotificationDate) {
      return today;
    }

    const oldestDateInSelectedTimezone = isLocalTime
      ? oldestNotificationDate
      : toZonedTime(
          fromZonedTime(oldestNotificationDate, airportTimeZone),
          "UTC"
        );

    return startOfDay(oldestDateInSelectedTimezone);
  }, [airportTimeZone, isLocalTime, notifications, today]);

  useEffect(() => {
    if (!isDateRangeModified) {
      setFromDate((currentFromDate) =>
        currentFromDate.getTime() === initialFromDate.getTime()
          ? currentFromDate
          : initialFromDate
      );
      setToDate((currentToDate) =>
        currentToDate.getTime() === today.getTime() ? currentToDate : today
      );
    }
  }, [initialFromDate, isDateRangeModified, today]);

  const filteredNotifications = useMemo(() => {
    const fromDateBoundary = startOfDay(fromDate);
    const toDateBoundary = endOfDay(toDate);

    const updatedFromDate = isLocalTime
      ? fromDateBoundary
      : toZonedTime(fromZonedTime(fromDateBoundary, "UTC"), airportTimeZone);

    const updatedToDate = isLocalTime
      ? toDateBoundary
      : toZonedTime(fromZonedTime(toDateBoundary, "UTC"), airportTimeZone);

    return notifications.filter((notification) => {
      if (
        filters.severity !== "ALL" &&
        notification.severity.toUpperCase() !== filters.severity
      ) {
        return false;
      }

      if (
        filters.category !== "All" &&
        notification.category.toLowerCase() !== filters.category.toLowerCase()
      ) {
        return false;
      }

      return isNotificationInDateRange(
        notification,
        updatedFromDate,
        updatedToDate
      );
    });
  }, [
    airportTimeZone,
    filters.category,
    filters.severity,
    fromDate,
    isLocalTime,
    notifications,
    toDate,
  ]);

  const sortedNotifications = useMemo(
    () =>
      [...filteredNotifications].sort(
        (firstNotification, secondNotification) => {
          const firstTimestamp = new Date(
            firstNotification.timestamp
          ).getTime();
          const secondTimestamp = new Date(
            secondNotification.timestamp
          ).getTime();

          return sort === "NEWEST_FIRST"
            ? secondTimestamp - firstTimestamp
            : firstTimestamp - secondTimestamp;
        }
      ),
    [filteredNotifications, sort]
  );

  const formattedNotifications = useMemo(
    () =>
      formatFilteredNotifications(
        sortedNotifications,
        isLocalTime,
        airportTimeZone,
        currentTime,
        true
      ),
    [airportTimeZone, currentTime, isLocalTime, sortedNotifications]
  );

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(
      screenNameActions.setScreenName(PageEnum.BreadcrumbEnum.Notifications)
    );
  }, [dispatch]);

  const handleFromDateChange = (nextFromDate: Date | null): void => {
    const resolvedFromDate = nextFromDate ?? today;

    setIsDateRangeModified(true);
    setFromDate(resolvedFromDate);

    if (toDate.getTime() < resolvedFromDate.getTime()) {
      setToDate(resolvedFromDate);
    }
  };

  const handleToDateChange = (nextToDate: Date | null): void => {
    setIsDateRangeModified(true);
    setToDate(nextToDate ?? today);
  };

  const handleResetFilters = (): void => {
    setFilters(DEFAULT_FILTERS);
    setSort(DEFAULT_SORT);
    setFromDate(initialFromDate);
    setToDate(today);
    setIsDateRangeModified(false);
  };

  const handleExpandedChange = (id: string): void => {
    setExpandedIds((currentIds) => {
      const nextIds = allowMultipleExpanded
        ? new Set(currentIds)
        : new Set<string>();

      if (currentIds.has(id)) {
        nextIds.delete(id);
      } else {
        nextIds.add(id);
      }

      return nextIds;
    });
  };

  const noDataCard = (
    <NoDataCard message={EMPTY_STATE_MESSAGE.Notifications} marginAll={true} />
  );

  return (
    <main className={mergeClasses(styles.root, isInTeams && styles.rootTeams)}>
      <h1>Notifications</h1>

      {isFetching ? (
        <Spinner size="large" style={{ margin: "auto" }} />
      ) : isError ? (
        noDataCard
      ) : (
        <>
          <NotificationHistoryHeader
            filteredCount={formattedNotifications.length}
            totalCount={notifications.length}
          />
          <NotificationHistoryFilters
            filters={filters}
            sort={sort}
            fromDate={fromDate}
            toDate={toDate}
            currentDate={today}
            onFiltersChange={setFilters}
            onSortChange={setSort}
            onFromDateChange={handleFromDateChange}
            onToDateChange={handleToDateChange}
            onReset={handleResetFilters}
          />
          <NotificationHistoryList
            notifications={formattedNotifications}
            expandedIds={expandedIds}
            onExpandedChange={handleExpandedChange}
          />
        </>
      )}
    </main>
  );
};

export default NotificationHistory;
